import { useState } from "react"
import { DateRange } from "react-day-picker"
import { SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { TrashSimple } from "@phosphor-icons/react"
import { startOfDay } from "date-fns"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { Button } from "@/components/ui/button"
import { DateRangeInput } from "@/components/ui/date-range-input"
import { DeprecatedConfirmationDialog } from "@/components/ui/deprecated-confirmation-dialog"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FormField } from "@/components/ui/form"
import { Input, TextArea } from "@/components/ui/input"
import { RadioGroup, RadioGroupItem, RadioGroupLabel } from "@/components/ui/radio-group"
import {
  LabelAndSelectValue,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { withErrorBoundary } from "@/hoc/with-error-boundary"
import { useToast } from "@/hooks/useToast"
import { DateOnly } from "@/lib/date-only"
import { textAreaDefaultMaxLength } from "@/models/common"
import { BirthDatePicker } from "@/modules/common/address-section/birth-date-picker"
import { LastModificationInfo } from "@/modules/patients/detail/last-modification-info"
import {
  ChLegalGuardianAttestationSchema,
  ChLegalGuardianAttestationSchemaType,
} from "@/modules/patients/detail/legal-guardian-attestation/ch/schema/ch-legal-guardian-attestation-schema"
import { chLegalGuardianAttestationFragment$key } from "@/modules/patients/detail/legal-guardian-attestation/ch/services/__generated__/chLegalGuardianAttestationFragment.graphql"
import { chLegalGuardianAttestationFragment } from "@/modules/patients/detail/legal-guardian-attestation/ch/services/chLegalGuardianAttestationFragment"
import { useChCreateLegalGuardianAttestationMutation } from "@/modules/patients/detail/legal-guardian-attestation/ch/services/useChCreateLegalGuardianAttestationMutation"
import { useChDeleteLegalGuardianAttestationMutation } from "@/modules/patients/detail/legal-guardian-attestation/ch/services/useChDeleteLegalGuardianAttestationMutation"
import { useChGenerateLegalGuardianDocumentMutation } from "@/modules/patients/detail/legal-guardian-attestation/ch/services/useChGenerateLegalGuardianDocumentMutation"
import { useChUpdateLegalGuardianAttestationMutation } from "@/modules/patients/detail/legal-guardian-attestation/ch/services/useChUpdateLegalGuardianAttestationMutation"
import { useLegalGuardianAttestationDrawerContext } from "@/modules/patients/detail/legal-guardian-attestation/store/useLegalGuardianAttestationDrawerStore"
import { usePatientFileContext } from "@/modules/patients/detail/store/usePatientStore"
import { useContextAppointment } from "@/modules/patients/hooks/useContextAppointment"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import {
  DOCUMENT_LANGUAGES,
  getDefaultDocumentLanguageValue,
  getDocumentLanguageLabel,
} from "@/modules/patients/libs/document-languages"
import { useDrawerStore } from "@/stores/useDrawerStore"

const today = startOfDay(new Date())

const ChLegalGuardianAttestationDrawer = withErrorBoundary(() => {
  const { t } = useTranslation()
  const patientId = usePatientParam()
  const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false)
  const [createLegalGuardianAttestation, isCreating] = useChCreateLegalGuardianAttestationMutation()
  const [updateLegalGuardianAttestation, isUpdating] = useChUpdateLegalGuardianAttestationMutation()
  const [deleteLegalGuardianAttestation] = useChDeleteLegalGuardianAttestationMutation()

  const { toast } = useToast()

  const progressNoteId = useLegalGuardianAttestationDrawerContext((state) => state.progressNoteId)
  const legalGuardianAttestationId = useLegalGuardianAttestationDrawerContext(
    (state) => state.legalGuardianAttestationId,
  )
  const legalGuardianAttestationRef = useLegalGuardianAttestationDrawerContext(
    (state) => state.legalGuardianAttestationRef,
  )
  const setLegalGuardianAttestation = useLegalGuardianAttestationDrawerContext(
    (state) => state.setLegalGuardianAttestation,
  )

  const [generateDocument, isGenerating] = useChGenerateLegalGuardianDocumentMutation()

  const patientFileInfo = usePatientFileContext((state) => state.patientFileInfo)

  const contextAppointment = useContextAppointment()
  const closeDrawer = useDrawerStore((state) => state.closeDrawer)
  const isEditMode = !!legalGuardianAttestationId
  const isAddMode = !isEditMode && patientId && progressNoteId
  const persistedData = useFragment<chLegalGuardianAttestationFragment$key>(
    chLegalGuardianAttestationFragment,
    legalGuardianAttestationRef as chLegalGuardianAttestationFragment$key,
  )
  const updatedAt = persistedData?.updatedAt ?? persistedData?.createdAt
  const updatedBy = persistedData?.updatedBy ?? persistedData?.createdBy

  const parsedData =
    isEditMode && persistedData
      ? ChLegalGuardianAttestationSchema.parse({
          ...persistedData,
          durationFrom: new Date(persistedData?.durationFrom),
          durationTo: new Date(persistedData?.durationTo),
          birthDate: new Date(persistedData?.birthDate),
          additionalInformation: persistedData?.additionalInformation ?? "",
        })
      : null

  const form = useForm<ChLegalGuardianAttestationSchemaType>({
    mode: "all",
    resolver: zodResolver(ChLegalGuardianAttestationSchema),
    defaultValues:
      isEditMode && parsedData
        ? {
            ...parsedData,
          }
        : {
            language: getDefaultDocumentLanguageValue(contextAppointment?.language),
            certificate: "FIRST",
            durationFrom: today,
            durationTo: undefined,
            firstName: "",
            lastName: "",
            birthDate: undefined,
            employer: "",
            additionalInformation: "",
          },
  })

  const {
    control,
    formState: { errors, isDirty },
    register,
    setValue,
    resetField,
    getValues,
  } = form

  useDirtyDrawerMaker(isDirty)

  const handleChangeDuration = (range?: DateRange) => {
    if (range?.from) {
      setValue("durationFrom", range.from, { shouldValidate: true, shouldDirty: true })
    }
  }

  const handleDurationInputChange = (_e: React.ChangeEvent<HTMLInputElement>, range?: DateRange) => {
    if (range?.from) setValue("durationFrom", range.from, { shouldValidate: true, shouldDirty: true })
    if (range?.to) setValue("durationTo", range.to, { shouldValidate: true, shouldDirty: true })
  }

  const handleCloseDrawer = () => {
    setLegalGuardianAttestation(null, null)
    closeDrawer()
  }

  const onSubmit: SubmitHandler<ChLegalGuardianAttestationSchemaType> = (data) => {
    if (!progressNoteId) return

    const { durationFrom, durationTo, birthDate, additionalInformation } = data

    const formattedData = {
      ...data,
      durationFrom: new DateOnly(durationFrom).toString(),
      durationTo: new DateOnly(durationTo).toString(),
      birthDate: new DateOnly(birthDate).toString(),
      additionalInformation: additionalInformation.length > 0 ? additionalInformation : null,
    }

    if (isAddMode) {
      createLegalGuardianAttestation({
        variables: {
          input: {
            patientId,
            progressNoteId,
            language: formattedData.language,
            durationFrom: formattedData.durationFrom,
            durationTo: formattedData.durationTo,
            certificate: formattedData.certificate,
            firstName: formattedData.firstName,
            lastName: formattedData.lastName,
            birthDate: formattedData.birthDate,
            employer: formattedData.employer,
            additionalInformation: formattedData.additionalInformation,
          },
        },
        onCompleted(response) {
          const id = response.ch_createAttestationGuardian.ch_AttestationGuardian?.id
          if (id) {
            handleGenerateDocument(id)
          }
        },
        updater(store, data) {
          try {
            const progressNoteRecordProxy = store.get(progressNoteId)
            const attestationGuardian = data?.ch_createAttestationGuardian?.ch_AttestationGuardian

            if (attestationGuardian?.id) {
              const newRecordProxy = store.get(attestationGuardian.id)
              if (newRecordProxy) {
                progressNoteRecordProxy?.setLinkedRecord(newRecordProxy, "ch_attestationGuardian")
              }
            }
          } catch (error) {
            console.error("Error while processing data:", error)
          }
        },
      })
    }

    if (isEditMode) {
      updateLegalGuardianAttestation({
        variables: {
          input: {
            id: legalGuardianAttestationId,
            language: formattedData.language,
            durationFrom: formattedData.durationFrom,
            durationTo: formattedData.durationTo,
            certificate: formattedData.certificate,
            firstName: formattedData.firstName,
            lastName: formattedData.lastName,
            birthDate: formattedData.birthDate,
            employer: formattedData.employer,
            additionalInformation: formattedData.additionalInformation,
          },
        },
        onCompleted(response) {
          const id = response.ch_updateAttestationGuardian.ch_AttestationGuardian?.id
          if (id) {
            handleGenerateDocument(id)
          }
        },
      })
    }
  }

  const handleDelete = () => {
    if (progressNoteId && legalGuardianAttestationId) {
      deleteLegalGuardianAttestation({
        variables: {
          input: {
            id: legalGuardianAttestationId,
          },
        },
        onCompleted() {
          handleCloseDrawer()
          handleOpenConfirmDialog(false)
          toast({
            description: t("patient.legalGuardianAttestation.deleteMessage.success"),
          })
        },
      })
    }
  }

  const handleOpenConfirmDialog = (isOpen: boolean) => {
    setIsOpenConfirmDialog(isOpen)
  }

  const handleGenerateDocument = (legalGuardianId: string) => {
    if (patientFileInfo && progressNoteId) {
      generateDocument({
        params: {
          id: legalGuardianId,
        },
        onCompleted() {
          handleCloseDrawer()
        },
      })
    }
  }

  return (
    <>
      <Drawer open onOpenChange={handleCloseDrawer}>
        <DrawerContent>
          <DrawerClose className="top-2 right-2" />
          <DrawerHeader className="mb-4 pr-4" data-testid="legal-guardian-attestation-header">
            <DrawerTitle className="animate-fade-in-bottom text-on-surface-subdued text-sm font-medium">
              {t("patient.legalGuardianAttestation.attestationForLegalGuardian")}
            </DrawerTitle>
          </DrawerHeader>
          <DrawerBody className="flex gap-4 pt-0.5" data-testid="legal-guardian-attestation-body">
            <FormField
              control={control}
              name="language"
              render={({ field: { onChange, value, ref } }) => (
                <div className="mb-4">
                  <Select value={value} onValueChange={onChange} {...ref}>
                    <SelectTrigger className="w-56.5">
                      <SelectValue asChild>
                        <LabelAndSelectValue
                          data-testid="document-language"
                          label={t("patient.therapyPrescription.documentLanguage")}
                          value={t(getDocumentLanguageLabel(value))}
                        />
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {DOCUMENT_LANGUAGES.map((language) => (
                        <SelectItem
                          value={language.value}
                          key={language.value}
                          data-testid={`document-language-${language.value.toLocaleLowerCase()}`}>
                          {t(language.label)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            />
            <FormField
              control={control}
              name="durationTo"
              render={({ field: { value, onChange } }) => {
                return (
                  <DateRangeInput
                    data-testid="duration-input"
                    value={{ from: getValues("durationFrom"), to: value }}
                    onSelectDays={(range) => {
                      handleChangeDuration(range)
                      onChange(range?.to ?? null)
                    }}
                    onInputChange={handleDurationInputChange}
                    label={t("patient.legalGuardianAttestation.duration")}
                    error={errors.durationFrom?.message ?? errors.durationTo?.message}
                    translateError
                    required
                  />
                )
              }}
            />
            <div className="mb-4 grid gap-2">
              <p className="text-on-surface-subdued text-sm">{t("patient.sickLeaveCertificate.typeOfCertificate")}</p>
              <FormField
                control={control}
                name="certificate"
                render={({ field: { value, onChange } }) => (
                  <RadioGroup
                    className="flex flex-wrap"
                    data-testid="certificate-radio"
                    value={value}
                    onValueChange={(value) => onChange(value)}>
                    <RadioGroupItem value="FIRST" data-testid="first-certificate-radio-item">
                      <RadioGroupLabel>{t("patient.legalGuardianAttestation.firstCertificate")}</RadioGroupLabel>
                    </RadioGroupItem>
                    <RadioGroupItem value="FOLLOW_UP" data-testid="follow-up-certificate-radio-item">
                      <RadioGroupLabel>{t("patient.legalGuardianAttestation.followUpCertificate")}</RadioGroupLabel>
                    </RadioGroupItem>
                  </RadioGroup>
                )}
              />
            </div>
            <div className="grid gap-2">
              <p className="text-on-surface text-base font-medium tracking-tight">
                {t("patient.legalGuardianAttestation.legalGuardian")}
              </p>
              <div className="mt-2 grid gap-4">
                <div className="grid grid-cols-2 gap-5">
                  <Input
                    {...register("firstName")}
                    label={t("patient.legalGuardianAttestation.firstName")}
                    required
                    error={errors.firstName?.message}
                    translateError
                    errorClasses="truncate"
                    data-testid="first-name-input"
                  />
                  <Input
                    {...register("lastName")}
                    label={t("patient.legalGuardianAttestation.lastName")}
                    required
                    error={errors.lastName?.message}
                    translateError
                    errorClasses="truncate"
                    data-testid="last-name-input"
                  />
                </div>
                <FormField
                  control={control}
                  name="birthDate"
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <BirthDatePicker
                      defaultValue={value}
                      error={error?.message}
                      onSelect={onChange}
                      onClear={() => resetField("birthDate")}
                    />
                  )}
                />
                <Input
                  {...register("employer")}
                  label={t("patient.legalGuardianAttestation.employerOfLegalGuardian")}
                  required
                  error={errors.employer?.message}
                  translateError
                  errorClasses="truncate"
                  data-testid="employer-input"
                />
                <div className="mt-4">
                  <TextArea
                    {...register("additionalInformation")}
                    label={t("patient.legalGuardianAttestation.additionalInformation")}
                    error={errors.additionalInformation?.message}
                    translateError
                    errorClasses="truncate"
                    data-testid="additional-information-input"
                    textLimit={textAreaDefaultMaxLength}
                  />
                </div>
              </div>
            </div>
          </DrawerBody>
          <DrawerFooter className="flex-wrap gap-2" data-testid="legal-guardian-attestation-footer">
            {isEditMode && (
              <div className="flex gap-2">
                {updatedAt && updatedBy && <LastModificationInfo updatedAt={updatedAt} updatedBy={updatedBy} />}
                <button onClick={() => handleOpenConfirmDialog(true)} data-testid="attestation-delete-button">
                  <TrashSimple className="text-on-surface-subdued" />
                </button>
              </div>
            )}
            <div className="ml-auto flex gap-2">
              <Button variant="secondary" data-testid="cancel-button" onClick={handleCloseDrawer}>
                {t("common.button.cancel")}
              </Button>
              <Button
                className="min-w-16.25"
                disabled={isEditMode && !isDirty}
                data-testid="attestation-save-button"
                onClick={() => {
                  FaroInsightsWrapper.pushEvent("documents_legalGuardian_save")
                  form.handleSubmit(onSubmit)()
                }}
                isLoading={isCreating || isUpdating || isGenerating}>
                {t("common.button.save")}
              </Button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <DeprecatedConfirmationDialog
        title={t("patient.legalGuardianAttestation.deleteConfirmTitle")}
        content={t("episodeOfCare.deleteProgressNote.content")}
        open={isOpenConfirmDialog}
        onConfirm={handleDelete}
        confirmVariant="danger"
        confirmText={t("common.button.delete")}
        cancelText={t("common.button.keep")}
        onOpenChange={handleOpenConfirmDialog}
      />
    </>
  )
}, "ChLegalGuardianAttestationDrawer")

export { ChLegalGuardianAttestationDrawer }
